import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { APIHttpError } from '@/helpers/error/apiHttpError';

interface LoginFlowErrorMessage {
	message: string;
	isLoggable: boolean;
	logBody?: object;
}

export const getNextRouteFromNewSigninState = (
	newState: string,
	oldRoute: RouteLocationNormalizedLoaded
) => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { returnUrl } = oldRoute.query;

	switch (newState) {
		case 'mfaRequesting':
			return {
				path: `${import.meta.env.VITE_SIGNIN_BASEURL}requestpasscode`,
				query: {}
			};
		case 'mfaSubmitting':
			return {
				path: `${import.meta.env.VITE_SIGNIN_BASEURL}sendpasscode`,
				query: {}
			};
		case 'resetPassword':
			return {
				path: `${import.meta.env.VITE_SIGNIN_BASEURL}resetpassword`,
				query: {}
			};
		case 'resetPasswordByPasscode':
			return {
				path: `${import.meta.env.VITE_SIGNIN_BASEURL}resetpasswordbypasscode`,
				query: {}
			};
		case 'recoverUserName':
			return {
				path: `${import.meta.env.VITE_SIGNIN_BASEURL}recoverUserName`,
				query: {}
			};
		case 'complete':
			return {
				path: `${import.meta.env.VITE_SIGNIN_BASEURL}loading`
			};
		default:
			return {
				path: import.meta.env.VITE_SIGNIN_BASEURL
			};
	}
};

export const getFriendlyErrorMessage = async (
	error: Error
): Promise<LoginFlowErrorMessage> => {
	if (error instanceof TypeError) {
		if (error.message === 'Failed to fetch') {
			return {
				message:
					'There was a problem contacting the login server. If this persists, contact support.',
				isLoggable: false
			};
		} else {
			return {
				message: 'Unknown error.',
				isLoggable: false
			};
		}
	} else if (error instanceof APIHttpError) {
		try {
			const body = await (error as APIHttpError).response.json();

			if (body.next && body.next.failedLoginAttemptLimitReached) {
				return {
					message:
						'After too many failed attempts, your login is locked out. Please contact support for unlock assistance.',
					isLoggable: false
				};
			} else if (body.next && body.next.isLoginFailure) {
				return {
					message:
						'Login failure. Please check login and/or password.',
					isLoggable: false
				};
			} else if (body.error) {
				return {
					message: body.error,
					isLoggable: false
				};
			} else {
				return {
					message: 'Unknown login failure.',
					isLoggable: true,
					logBody: body
				};
			}
		} catch (error) {
			return {
				message: 'Unknown login failure.',
				isLoggable: true
			};
		}
	} else {
		return {
			message: 'Unknown login failure.',
			isLoggable: false
		};
	}
};
